import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { servicesContent } from "@/data/servicesContent";
import PortalHeader from "@/components/portal/PortalHeader";
import PortalStats from "@/components/portal/PortalStats";
import PortalServiceCatalog from "@/components/portal/PortalServiceCatalog";
import PortalProjectTracker from "@/components/portal/PortalProjectTracker";
import PortalAssetVault from "@/components/portal/PortalAssetVault";
import StartInstantlyDrawer from "@/components/portal/StartInstantlyDrawer";
import BookStrategyCallModal from "@/components/portal/BookStrategyCallModal";
import { ProposalPanel } from "@/components/portal/ProposalPanel";
import { ThemeToggle, useTheme } from "@/components/ThemeContext";

const formatShortDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

export default function ClientPortal() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [projects, setProjects] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Drawer + modal state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerService, setDrawerService] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalService, setModalService] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [projRes, subRes, assetRes] = await Promise.allSettled([
          api.get("/projects"),
          api.get("/subscriptions/me").catch(() => ({ data: [] })),
          api.get("/portal/assets").catch(() => ({ data: [] })),
        ]);
        if (projRes.status === "fulfilled") setProjects(projRes.value.data || []);
        if (subRes.status === "fulfilled") setSubscriptions(subRes.value.data || []);
        if (assetRes.status === "fulfilled") setAssets(assetRes.value.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Compute quick stats
  const stats = useMemo(() => {
    const active = projects.filter((p) => p.status === "active" || p.status === "planning");
    const monthlySpend = (subscriptions || []).reduce((sum, s) => sum + (s.amount || 0), 0);
    const pendingApprovals = assets.filter((a) =>
      /brief|proposal|pending|approval/i.test(a.filename || "")
    ).length;
    // Next deliverable heuristic: highest sprint_progress not-yet-100
    const inProgress = [...active].sort(
      (a, b) => (b.sprint_progress || 0) - (a.sprint_progress || 0)
    );
    const next = inProgress[0];
    const nextDate = next?.next_milestone_due
      ? formatShortDate(next.next_milestone_due)
      : next
      ? "In sprint"
      : "—";
    return {
      activeProjects: active.length,
      monthlySpend,
      pendingApprovals,
      nextDeliverable: nextDate,
    };
  }, [projects, subscriptions, assets]);

  const filteredProjects = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter(
      (p) =>
        p.name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term) ||
        p.status?.toLowerCase().includes(term)
    );
  }, [projects, search]);

  const openStartInstantly = (slug) => {
    const svc = servicesContent[slug];
    if (!svc) return;
    setDrawerService({ slug, svc });
    setDrawerOpen(true);
  };

  const openBookCall = (slug) => {
    const svc = slug ? servicesContent[slug] : null;
    setModalService(svc);
    setModalOpen(true);
  };

  return (
    <div className={`min-h-screen ${theme === "light" ? "bg-slate-50 text-slate-900" : "bg-[#0B0C10] text-[#F5F5F7]"}`} data-testid="client-portal-page">
      <PortalHeader search={search} onSearch={setSearch} />

      {/* Welcome banner */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-2" data-testid="portal-welcome">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className={`font-mono text-xs uppercase tracking-[0.3em] mb-3 ${theme === "light" ? "text-red-600" : "text-[#B91C2C]"}`}>
              // CLIENT.PORTAL · /home
            </div>
            <h1 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter">
              Welcome back,{" "}
              <span className={theme === "light" ? "text-red-600" : "text-[#FF3355]"}>
                {user?.name?.split(" ")[0] || "operator"}.
              </span>
            </h1>
            <p className={`mt-2 text-sm ${theme === "light" ? "text-slate-600" : "text-[#C5C6C7]"}`}>
              {user?.company ? `${user.company} · ` : ""}
              <span className="text-emerald-500">Systems green.</span> One dashboard for everything you own with us.
            </p>
          </div>
          <ThemeToggle />
        </div>
      </section>

      <PortalStats stats={stats} />

      {/* Proposal panel */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-4" data-testid="portal-proposal-section">
        <ProposalPanel mode="client" />
      </section>

      <PortalProjectTracker projects={filteredProjects} loading={loading} />

      <PortalServiceCatalog
        onStartInstantly={openStartInstantly}
        onBookCall={openBookCall}
        search={search}
      />

      <PortalAssetVault />

      {/* Drawers / Modals */}
      <StartInstantlyDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slug={drawerService?.slug}
        svc={drawerService?.svc}
      />
      <BookStrategyCallModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        svc={modalService}
      />

      {/* Bottom spacer */}
      <div className="h-16" />
    </div>
  );
}
